/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// actions 
import { discardAddOnsSettings, addOnsSettingsChanged, createAddOnsSetting, updateAddOnsSetting } from 'state/actions/commentingState/addOnsStatus';
// material
import { CopySnippetField, Switch, TextField, sendErrorMessage } from '@insticator/insticator-ui'; // iui-material
import AddOnController from 'material/expandablesection/controllers/addoncontroller/addoncontroller';
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription';
import TopArticles from 'material/community/toparticles/toparticles';
import Timeframe from 'material/community/timeframe/timeframe';
import PlaceholderImages from 'material/community/placeholderimages/placeholderimages';
import TopCommenters from 'material/community/topcommenters/topcommenters';
import CustomColorScheme from './customcolorscheme/customcolorscheme';
// utils
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/commenting/addons/communitysection/communitysection_styles';


const CommunitySection = (props) => {

  const isImageChanged = useSelector(({ commentingState }) => commentingState.addOnsStatus.isImageChanged);
  const placeholderImageUploading = useSelector(({ commentingState }) => commentingState.addOnsStatus.isUploadingImage);
  const community = useSelector(({commentingState}) => commentingState.addOnsStatus.addOnsData.community);
  const isCommunitySettingCreated = useSelector(({commentingState}) => commentingState.addOnsStatus.isSettingCreated.community);
  const commentingUnitConfigurationUUID  = useSelector(({commentingState}) => commentingState.configStatus.commentingUnitConfigurationUUID);
  const isCommunitySettingChanged = useSelector(({commentingState})=> commentingState.addOnsStatus.isSettingChanged.community);
  const isCommunityColorsValid = useSelector(({ commentingState}) => commentingState.addOnsStatus.isCommunityColorsValid);

  const dispatch = useDispatch();

  // component state
  const [isOpened, setIsOpened] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const communityTitleTest = /^(?!\s)[\WA-Za-z0-9'\-\s]{1,64}$/;
  const validateCommunityTitle = e => settingsChanged(
    { addOnsType: 'community', 
      property: 'title', 
      value: {
        ...community.title,
        valid: communityTitleTest.test(e.target.value)
      }
    }
  );
  
  const collapse = () => {
    setIsOpened(false);
    setIsProcessing(false);
  }

  const toggleSwitch = () => {
    const addOnsSetting = {
      path: 'community',
      type: 'community', 
      request: {
        ...community,
        title: community.title.value,
        topArticlesDaysTimeFrame: community.topArticlesDaysTimeFrame.value,
        isCommunityEnabled: !community.isCommunityEnabled,
        commentingUnitConfigurationUUID
      }
    };

    if(!isCommunitySettingCreated) {
      delete addOnsSetting.request.uuid;
      dispatch(createAddOnsSetting(addOnsSetting))
    } else {
      dispatch(
        updateAddOnsSetting({
          ...addOnsSetting,
          configurationUUID: community.uuid  
        })
      );
    }

    if(community.isCommunityEnabled) 
      setIsOpened(false);
    
    dispatch(
      addOnsSettingsChanged({
        addOnsType: 'community', 
        property: 'isCommunityEnabled', 
        value: !community.isCommunityEnabled
      })
    );
  }

  const handleCancel = () => {
    dispatch(
        discardAddOnsSettings({
          addOnsType: 'community', 
          ignoreProperty: {
            name: 'isCommunityEnabled', 
            value: community.isCommunityEnabled
          }
        })
    );
  }

  const handleSave = () => {
    if (community.placeholderImageType === 'standard' && (community.placeholderImageUrl !== null || community.placeholderImageUrl !== '')) {
      community.placeholderImageUrl = null;
    }
    setIsProcessing(true);
    dispatch(
      updateAddOnsSetting({
        path: 'community',
        type: 'community', 
        configurationUUID: community.uuid,
        request: {
          ...community,
          title: community.title.value,
          topArticlesDaysTimeFrame: community.topArticlesDaysTimeFrame.value
        },
        showSuccessMessage: true,
        label: 'Community',
        callback: collapse
      })
    );
  }

  const toggleSectionSwitch = property => {
    dispatch(
        addOnsSettingsChanged({
          addOnsType: 'community',
          property,
          value: !community[property]
        })
    );
  }

  const settingsChanged = ({addOnsType, property, value}) => {
    dispatch(addOnsSettingsChanged({addOnsType, property, value}));
  }

  const isSectionValid = () => {
    let isValid = true;
    isValid = isValid && community.title.valid && community.topArticlesDaysTimeFrame.valid;
    return isValid
  }

  const saveCommunity = () => {
    if(!isSectionValid()){
      return dispatch(sendErrorMessage('Cannot save: invalid fields'));
    }
    handleSave();
  }

  const controller = () => (
    <AddOnController
      title={'Community'}
      image={'illustration-community.svg'}
      description={`Community shows the most popular discussions and top commenters on your website. 
      Enabling Community allows you to recirculate your most popular content as well as 
      highlight your most engaged commenters.`}
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      toggle={community.isCommunityEnabled}
      toggleClick={toggleSwitch}
    />
  );

  return (
    <ExpandableSection
      className={`communitysection ${props.classes.communitysection}`}
      controller={controller()}
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      processing={isProcessing}
      handleCancel={handleCancel}
      handleSave={saveCommunity}
      // TODO: Comment current disableSave prop and always disable save button for now
      // disableSave={placeholderImageUploading || (!isImageChanged && !isCommunitySettingChanged) || !isCommunityColorsValid || (community.placeholderImageType === 'custom' && (!community.placeholderImageUrl || community.placeholderImageUrl === ''))}
      disableSave={true}
    >

      <SectionCard>
        <SectionGroup>
          <SectionGroupDescription />
            <CopySnippetField 
              title={'Community Add-On Code'} 
              description={
              <div>
                Place the Community Add-On code inside of the <strong>&lt;body&gt;</strong> tag 
                in your HTML source code where you want the Community Add-On to appear in your site.
              </div>
              } 
              codeSnippet={community.code} 
            />
        </SectionGroup>
      </SectionCard>
      {/*  TODO: Hide the community settings for now
      <SectionCard>
        <SectionGroup>
          <SectionGroupDescription title='Title' />
          <TextField 
            name={'communityTitle'}
            type={'text'}
            required={true}
            placeholder={'Popular in the community'} 
            value={community.title.value}
            valid={community.title.valid}
            handleBlur={validateCommunityTitle}
            handleChange={e => 
              dispatch(
                addOnsSettingsChanged({
                  addOnsType: 'community', 
                  property: 'title', 
                  value: {
                    ...community.title,
                    value: e.target.value
                  }
                })
              )
            }
          />
        </SectionGroup>

        <SectionGroup row>
          <SectionGroupDescription
            title='Top articles'
            description='Define how many top articles should be loaded by default.'
          />
          <Switch
            on={community.showTopArticles}
            handleClick={() => toggleSectionSwitch('showTopArticles')}
            variant='secondary'
          />
        </SectionGroup>


        {community.showTopArticles && 
          <>
          <SectionGroup secondary>
            <SectionGroupDescription />
            <TopArticles
              data={{
                topArticlesInitialLoadLimit: community.topArticlesInitialLoadLimit
              }} 
              onChange={value => settingsChanged({addOnsType: 'community', property: 'topArticlesInitialLoadLimit', value })}
            />
          </SectionGroup>
          
          <SectionGroup secondary>
            <SectionGroupDescription title={'Top articles timeframe'} />
            <Timeframe
              data={{
                topArticlesDaysTimeFrame: community.topArticlesDaysTimeFrame
              }} 
              onChange={value => settingsChanged({addOnsType: 'community', property: 'topArticlesDaysTimeFrame', value })}
            />
          </SectionGroup>

          <SectionGroup secondary>
            <SectionGroupDescription title={'Placeholder images for the articles without a header image'} />
            <PlaceholderImages
              handleSelect={value => settingsChanged({addOnsType: 'community', property: 'placeholderImageType', value})}
              selectedPlaceholderImageType={community.placeholderImageType}
              type={'STAND_ALONE_COMMUNITY'}
              placeholderImageUrl={community.placeholderImageUrl}
              onChange={value => settingsChanged({addOnsType: 'community', property: 'placeholderImageUrl', value })}
            />
          </SectionGroup>
          </>
        }

        <SectionGroup row>
          <SectionGroupDescription
            title='Top commenters'
            description='Define how many top commenters should be loaded by default.'
          />
          <Switch
            on={community.showTopCommenters}
            handleClick={() => toggleSectionSwitch('showTopCommenters')}
            variant='secondary'
          />
        </SectionGroup>

        {community.showTopCommenters && 
          <SectionGroup secondary>
            <SectionGroupDescription />
            <TopCommenters 
              data={{
                topCommentersInitialLoadLimit: community.topCommentersInitialLoadLimit
              }} 
              onChange={value => settingsChanged({addOnsType: 'community', property: 'topCommentersInitialLoadLimit', value })}
            />                  
          </SectionGroup>
        }
      </SectionCard>

      <SectionCard>
        <SectionGroup row>
          <SectionGroupDescription
            title='Custom color scheme'
            description='Define your own color scheme if you want to add personality to the Community'
          />
          <Switch
            on={community.isCustomColorEnabled}
            handleClick={() => toggleSectionSwitch('isCustomColorEnabled')}
            variant='secondary'
          />
        </SectionGroup>

        {community.isCustomColorEnabled && 
          <>
            <SectionGroup secondary>
              <SectionGroupDescription />
              { isOpened &&
                <CustomColorScheme 
                  type={'community'} 
                  data={community.customColorSchema} 
                  onChange={value => settingsChanged({addOnsType: 'community', property: 'customColorSchema', value })}
                  onValidate={isValid => settingsChanged({addOnsType: null, property: 'isCommunityColorsValid', value: isValid})}
                />
              }
            </SectionGroup>
          </>
         }
      </SectionCard>
      */}

    </ExpandableSection>
  );
}

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(CommunitySection)